import type {
  InspectionReportDocument,
  InspectionPage,
  InspectionLayoutId,
  PhotoSlot,
  RichText,
  Annotation,
  BlockVAlign,
} from '@jobos/shared';

// Pure, immutable operations on an inspection report document. The editor calls
// these and feeds the result into autosave. Kept side-effect free so they're
// trivially unit-testable and never mutate React state in place.

export function createPage(layoutType: InspectionLayoutId): InspectionPage {
  return { id: crypto.randomUUID(), layoutType, slots: {}, blocks: {} };
}

export function addPage(doc: InspectionReportDocument, layoutType: InspectionLayoutId): InspectionReportDocument {
  return { ...doc, pages: [...doc.pages, createPage(layoutType)] };
}

export function removePage(doc: InspectionReportDocument, pageId: string): InspectionReportDocument {
  return { ...doc, pages: doc.pages.filter((p) => p.id !== pageId) };
}

const reidAnnotations = (annotations: Annotation[]): Annotation[] =>
  annotations.map((a) => ({ ...a, id: crypto.randomUUID() }));

export function duplicatePage(doc: InspectionReportDocument, pageId: string): InspectionReportDocument {
  const index = doc.pages.findIndex((p) => p.id === pageId);
  if (index === -1) return doc;
  const source = doc.pages[index]!;
  const clone = structuredClone(source);
  // Fresh ids everywhere the document identifies something: the page itself and
  // every annotation on it. Annotation ids are what selection and delete key
  // off, so a copy that reused them would make two pages answer to one id.
  const copy: InspectionPage = {
    ...clone,
    id: crypto.randomUUID(),
    slots: Object.fromEntries(
      Object.entries(clone.slots).map(([key, slot]) => [
        key,
        { ...slot, annotations: reidAnnotations(slot.annotations) },
      ]),
    ),
    ...(clone.annotations ? { annotations: reidAnnotations(clone.annotations) } : {}),
  };
  const pages = [...doc.pages];
  pages.splice(index + 1, 0, copy);
  return { ...doc, pages };
}

export function reorderPages(
  doc: InspectionReportDocument,
  fromIndex: number,
  toIndex: number,
): InspectionReportDocument {
  if (fromIndex === toIndex) return doc;
  const pages = [...doc.pages];
  const [moved] = pages.splice(fromIndex, 1);
  if (!moved) return doc;
  pages.splice(toIndex, 0, moved);
  return { ...doc, pages };
}

function updatePage(
  doc: InspectionReportDocument,
  pageId: string,
  updater: (page: InspectionPage) => InspectionPage,
): InspectionReportDocument {
  return {
    ...doc,
    pages: doc.pages.map((p) => (p.id === pageId ? updater(p) : p)),
  };
}

function updateSlot(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  updater: (slot: PhotoSlot) => PhotoSlot,
): InspectionReportDocument {
  return updatePage(doc, pageId, (page) => {
    const existing: PhotoSlot =
      page.slots[slotKey] ?? { fileId: null, transform: { zoom: 1, offsetX: 0, offsetY: 0 }, annotations: [] };
    return { ...page, slots: { ...page.slots, [slotKey]: updater(existing) } };
  });
}

/**
 * Point a slot at a file, or empty it with null. A different photo means the
 * framing no longer applies, so zoom/pan reset and any legacy slot-bound
 * annotations (which were drawn on the photo that just left) go with it. The
 * caption stays: it usually describes the subject, not the particular shot.
 * Page-level annotations are not touched — they belong to the page, not a slot.
 */
export function setSlotPhoto(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  fileId: string | null,
): InspectionReportDocument {
  return updateSlot(doc, pageId, slotKey, (slot) =>
    slot.fileId === fileId
      ? slot
      : { ...slot, fileId, transform: { zoom: 1, offsetX: 0, offsetY: 0 }, annotations: [] },
  );
}

/** Empty a slot back to a blank frame. See setSlotPhoto for what goes with it. */
export function clearSlotPhoto(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
): InspectionReportDocument {
  return setSlotPhoto(doc, pageId, slotKey, null);
}

// Limit a normalized pan offset so the (zoomed) photo edge can't be dragged
// inside the slot frame. At zoom z the photo overflows the slot by (z-1), so
// each side can pan at most (z-1)/2. At zoom 1 there's no overflow → pinned to 0.
export function clampPanOffset(value: number, zoom: number): number {
  const max = Math.max(0, (zoom - 1) / 2);
  // `|| 0` normalizes -0 → 0.
  return Math.max(-max, Math.min(max, value)) || 0;
}

// Apply a zoom while keeping offsets valid for the new zoom (re-clamps on zoom-out).
export function clampTransform(transform: PhotoSlot['transform']): PhotoSlot['transform'] {
  return {
    zoom: transform.zoom,
    offsetX: clampPanOffset(transform.offsetX, transform.zoom),
    offsetY: clampPanOffset(transform.offsetY, transform.zoom),
  };
}

export function setSlotTransform(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  transform: PhotoSlot['transform'],
): InspectionReportDocument {
  return updateSlot(doc, pageId, slotKey, (slot) => ({ ...slot, transform }));
}

export function setSlotCaption(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  caption: string,
): InspectionReportDocument {
  return updateSlot(doc, pageId, slotKey, (slot) => ({ ...slot, caption }));
}

/**
 * What copying a slot's photo puts on the editor's clipboard (NEW-220): the
 * photo and how it is framed, nothing else.
 *
 * `photoKey` travels with `fileId` deliberately. It is the retained legacy
 * field (see `photoSlotSchema`) and is only meaningful as a pair with the file
 * it describes — carrying just the id would leave a pasted-over slot holding
 * the replaced photo's key.
 *
 * The caption is not on the clip: it labels the slot in the report, not the
 * photograph, so it neither travels with a copy nor is destroyed by a cut.
 */
export type SlotPhotoClip = {
  fileId: string;
  photoKey?: string | null;
  transform: PhotoSlot['transform'];
};

/** The clip for a slot's photo, or null when the slot is empty. */
export function slotPhotoClip(page: InspectionPage, slotKey: string): SlotPhotoClip | null {
  const slot = page.slots[slotKey];
  if (!slot?.fileId) return null;
  return { fileId: slot.fileId, photoKey: slot.photoKey, transform: slot.transform };
}

/**
 * Drop a clipped photo into a slot, replacing whatever was there.
 *
 * The target's legacy per-slot annotations go: they were drawn on the photo
 * being replaced, so keeping them would leave marks floating over a different
 * image. Page-level annotations are positioned against the page rather than the
 * slot and are none of this function's business.
 */
export function pasteSlotPhoto(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  clip: SlotPhotoClip,
): InspectionReportDocument {
  return updateSlot(doc, pageId, slotKey, (slot) => ({
    ...slot,
    fileId: clip.fileId,
    photoKey: clip.photoKey ?? null,
    transform: clip.transform,
    annotations: [],
  }));
}

export function setSlotAnnotations(
  doc: InspectionReportDocument,
  pageId: string,
  slotKey: string,
  annotations: Annotation[],
): InspectionReportDocument {
  return updateSlot(doc, pageId, slotKey, (slot) => ({ ...slot, annotations }));
}

export function setPageAnnotations(
  doc: InspectionReportDocument,
  pageId: string,
  annotations: Annotation[],
): InspectionReportDocument {
  return updatePage(doc, pageId, (page) => ({ ...page, annotations }));
}

export function setBlock(
  doc: InspectionReportDocument,
  pageId: string,
  blockKey: string,
  value: RichText,
): InspectionReportDocument {
  return updatePage(doc, pageId, (page) => ({
    ...page,
    blocks: { ...page.blocks, [blockKey]: value },
  }));
}

export function setBlockVAlign(
  doc: InspectionReportDocument,
  pageId: string,
  blockKey: string,
  vAlign: BlockVAlign,
): InspectionReportDocument {
  return updatePage(doc, pageId, (page) => ({
    ...page,
    blockMeta: {
      ...page.blockMeta,
      [blockKey]: { ...(page.blockMeta?.[blockKey] ?? {}), vAlign },
    },
  }));
}

export function setHeader(
  doc: InspectionReportDocument,
  header: Partial<InspectionReportDocument['header']>,
): InspectionReportDocument {
  return { ...doc, header: { ...doc.header, ...header } };
}
